import React, { useEffect } from 'react';
import { Button, Card, InputNumber, Modal, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getLatestLineItems, updateHubspotLineItems } from '../../services/Apis'
import { commaSeparatedString } from '../../utils/utils';
import { currencySymbolsMap, useCurrency } from '../../context/CurrencyContext';

interface DataType {
    key: string;
    service: string;
    core: number;
    discount: number;
    net: number;
}

interface LineItem {
    line_item_id?: number | null;
    name: string;
    amount: number;
    currency?: string;
}

interface LineItemCompareTableProps {
    dataSrc: DataType[];
    hubspotLineItems: LineItem[];
    totalDealAmount: number;
    updatedPricingItems: LineItem[];
    handleAmountChange: (name: string, amount: number) => void;
    distinctServiceLines: string[];
}


const LineItemCompareTable: React.FC<LineItemCompareTableProps> = ({ dataSrc, totalDealAmount, hubspotLineItems, updatedPricingItems, handleAmountChange, distinctServiceLines }) => {

    const { currencySymbol } = useCurrency();
    const [combinedItems, setCombinedItems] = React.useState<any[]>([]);

    useEffect(() => {
        const combinedItems = Array.from(new Set([...hubspotLineItems.map(item => item.name), ...updatedPricingItems.map(item => item.name)])).map(name => {
            const hubspotItem = hubspotLineItems.find(item => item.name === name);
            const pricingItem = updatedPricingItems.find(item => item.name === name);
            return {
                name,
                hubspotAmount: hubspotItem ? hubspotItem.amount : '',
                pricingAmount: pricingItem ? pricingItem.amount : 0,
                hubspotAmountCurrency: hubspotItem && hubspotItem.currency ? currencySymbolsMap[hubspotItem.currency] : '$',
            };
        });
        setCombinedItems(combinedItems);
    }, [updatedPricingItems, hubspotLineItems]);

    const totalHubspotAmount = totalDealAmount;
    const totalPricingAmount = updatedPricingItems.reduce((sum, item) => sum + item.amount, 0);

    const columns: ColumnsType<any> = [
        {
            title: 'Line Item',
            dataIndex: 'name',
            key: 'name',
            align: 'center',
            render: (text: string) => <span className={text === 'Total Deal Amount' ? "bold-row" : ""}>{text}</span>,
        },
        {
            title: 'Amounts Currently in HubSpot',
            dataIndex: 'hubspotAmount',
            key: 'hubspotAmount',
            align: 'center',
            render: (text: string, record: any) => <span className={record.name === 'Total Deal Amount' ? "bold-row" : ""}>{text ? text.toString().includes('%') ? text : `${record.hubspotAmountCurrency} ${commaSeparatedString(text)}` : text}</span>,
        },
        {
            title: 'Amounts to Update from Pricing App',
            dataIndex: 'pricingAmount',
            key: 'pricingAmount',
            align: 'center',
            render: (text: number, record: any) => (
                <InputNumber
                    min={0}
                    value={text}
                    formatter={(value) => `${currencySymbol} ${commaSeparatedString(value)}`}
                    onChange={(value) => handleAmountChange(record.name, value ?? 0)}
                    style={{ width: 100, borderColor: 'blue' }}
                    className="bold-input"
                    disabled={record.name === 'Total Deal Amount' || distinctServiceLines.some(service => service === record.name)}
                />
            ),
        },
    ];

    return (
        <Table
            dataSource={[...combinedItems, { name: 'Total Deal Amount', hubspotAmount: totalHubspotAmount, pricingAmount: totalPricingAmount, hubspotAmountCurrency: currencySymbol }]}
            columns={columns}
            rowKey="name"
            pagination={false}
            size="small"
        />
    );
};

export default LineItemCompareTable;